import { parseCalendarDate } from "@paribus/shared-ui";
import { format, isValid, parseISO } from "date-fns";

import { DEFAULT_DATE_PATTERN } from "../constants";

export { parseCalendarDate };

/**
 * Returns the calendar date portion of an ISO datetime (substring before "T"),
 * or null for null/empty input. Used for stable string comparison of date fields.
 */
export function extractDate(value: string | null | undefined): string | null {
  if (value == null || value === "") return null;
  const datePart = value.split("T")[0];
  return datePart === "" ? null : datePart;
}

/**
 * Compares YYYY-MM-DD calendar dates. Null/empty values sort last.
 * Lexicographic order matches chronological order for ISO date strings.
 */
export function compareCalendarDates(
  a: string | null | undefined,
  b: string | null | undefined
): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
}

/**
 * Formats a date value to M/D/YY (month first, no leading zeros on month/day).
 * This is the centralized date formatting function to be used throughout the UI.
 *
 * @param dateValue - Date as ISO string, Date object, or timestamp
 * @param fallback - Fallback value if date is invalid (default: "")
 * @returns Formatted date or fallback value
 */
export const formatDate = (
  dateValue: string | Date | number | null | undefined,
  fallback: string = ""
): string => {
  if (!dateValue) return fallback;

  try {
    let date: Date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === "string") {
      if (dateValue.includes("T")) {
        date = parseISO(dateValue);
      } else {
        const parsed = parseCalendarDate(dateValue);
        if (!parsed) return fallback;
        date = parsed;
      }
    } else if (typeof dateValue === "number") {
      date = new Date(dateValue);
    } else {
      return fallback;
    }

    if (!isValid(date)) return fallback;
    return format(date, DEFAULT_DATE_PATTERN);
  } catch (error) {
    console.error("Error formatting date:", error);
    return fallback;
  }
};
